import { ApiError } from '@sourcito/schemas';
import type { ZodType } from 'zod';

export class ApiCallError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

interface ApiFetchOptions<T> extends RequestInit {
  // Use ZodType<T, any, any> rather than ZodSchema<T> so schemas with
  // `.default()` (where the input type differs from the output type) still
  // type-check at the call site.
  responseSchema?: ZodType<T, any, any>;
}

/**
 * Typed fetch with cookie credentials, ApiError parsing, and optional response
 * validation against a zod schema. All API calls in the SPA go through this.
 */
export async function apiFetch<T>(url: string, options: ApiFetchOptions<T> = {}): Promise<T> {
  const { responseSchema, headers: providedHeaders, ...rest } = options;

  const headers = new Headers(providedHeaders);
  if (rest.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(url, {
    credentials: 'include',
    ...rest,
    headers,
  });

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const parsed = ApiError.safeParse(json);
    const message = parsed.success
      ? parsed.data.error.message
      : `Request to ${url} failed with status ${res.status}`;
    throw new ApiCallError(res.status, message, json);
  }

  if (responseSchema) {
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new Error(`Response from ${url} failed validation: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  return json as T;
}
